import React, { useEffect, useMemo, useState } from 'react';
import { Card, Text } from 'v3/components';
import theme from 'v3/theme';
import { Col, Row } from 'react-bootstrap';
import { formatAddress, formatDateCustom, formatTime, pluralize } from 'v3/utils/formatter';
import moment from 'moment';
import { FaArrowRight, FaCheck, FaClock } from 'react-icons/fa';
import * as Styled from './styles';
import { useClientTravelDetail } from './context';

export function TravelRouteCard() {
  const { data, isFetching } = useClientTravelDetail();
  let origin = useMemo(() => data?.loads?.loadOrigins?.[0], [data?.loads?.loadOrigins]);

  const shouldReturnTravelRouteCard =
    data?.loads?.loadOrigins?.some((item) => item.arrivalTime || item.effectiveTime) ||
    data?.loads?.loadDestinations?.some((item) => item.arrivalTime || item.effectiveTime);

  const [waitingHours, setWaitingHours] = useState(null);
  const [waitingMinutes, setWaitingMinutes] = useState(null);

  const destinations = useMemo(
    () => data?.loads?.loadDestinations,
    [data?.loads?.loadDestinations],
  );

  const isOriginCompleted = useMemo(() => origin?.arrivalTime, [data?.travelStatus?.id, origin]);

  useEffect(() => {
    const points = [origin, ...(destinations || [])];

    const totalMinutes = points.reduce((acc, point) => {
      if (!point?.arrivalTime || !point?.effectiveTime) {
        return acc;
      }

      const diff = moment(point.effectiveTime).diff(moment(point.arrivalTime), 'minutes');
      if (!Number.isFinite(diff) || diff < 0) {
        return acc;
      }

      return acc + diff;
    }, 0);

    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minutes = String(totalMinutes % 60).padStart(2, '0');

    setWaitingHours(hours);
    setWaitingMinutes(minutes);
  }, [origin, destinations]);
  const hasStartSchedule = !!(
    data?.loads?.loadOrigins?.[0]?.startSchedule ||
    data?.loads?.loadDestinations?.map((item) => item.startSchedule)[0] !== null
  );
  const originColumnMd = shouldReturnTravelRouteCard
    ? hasStartSchedule
      ? 3
      : 4
    : hasStartSchedule
    ? 3
    : 5;
  const schedulesColumnMd = 12 - originColumnMd;

  return (
    <Card
      header={
        <Text type="header" color={theme.colors.dark}>
          Rota da viagem
        </Text>
      }
      loading={isFetching}
    >
      <Row
        className="no-gutters"
        style={{ borderBottom: `1px solid ${theme.colors.medium_gray}`, marginBottom: '8px' }}
      >
        <Col md={originColumnMd} xs={12} className="px-0 pb-2">
          <Text type="body" color="dark" weight={600}>
            Origem
          </Text>
        </Col>
        <Col md={schedulesColumnMd} className="d-none d-sm-block px-0 pb-2">
          {shouldReturnTravelRouteCard ? (
            <Styled.RouteColumnsGrid $hasStartSchedule={hasStartSchedule}>
              <Styled.RouteGridCell>
                <Text type="body" color="dark" weight={600}>
                  Agendado
                </Text>
              </Styled.RouteGridCell>
              <Styled.RouteGridCell>
                <Text type="body" color="dark" weight={600}>
                  Chegada
                </Text>
              </Styled.RouteGridCell>
              <Styled.RouteGridCell />
              <Styled.RouteGridCell>
                <Text type="body" color="dark" weight={600}>
                  Efetivado
                </Text>
              </Styled.RouteGridCell>
            </Styled.RouteColumnsGrid>
          ) : (
            <Text as="div" type="body" color="dark" weight={600} className="text-center">
              Agendado
            </Text>
          )}
        </Col>
      </Row>
      <Row className="mb-3 no-gutters" style={{ display: 'flex', alignItems: 'stretch' }}>
        <LocationItem
          id={origin?.id}
          isOrigin
          action="Coleta"
          isCompleted={isOriginCompleted}
          address={origin ? formatAddress(origin) : ''}
          rawAddress={origin}
          scheduledTime={origin?.scheduledTime}
          arrivalTime={origin?.arrivalTime}
          startSchedule={origin?.startSchedule}
          hasStartSchedule={hasStartSchedule}
          effectiveTime={origin?.effectiveTime}
          client={origin?.client}
          shouldReturnTravelRouteCard={shouldReturnTravelRouteCard}
          originColumnMd={originColumnMd}
        />
      </Row>
      <Styled.SectionTitle>
        <Text type="body" color="dark" weight={600}>
          {pluralize('Destino', destinations?.length)}
        </Text>
      </Styled.SectionTitle>
      {destinations?.map((destination) => {
        const isCompleted = destination.arrivalTime && destination.effectiveTime;

        const action = actions[destination?.action?.toLowerCase()] || 'Coleta';

        return (
          <Row key={destination.id} className="no-gutters" style={{ display: 'flex', alignItems: 'stretch' }}>
            <LocationItem
              key={destination.id}
              id={destination.id}
              action={action}
              isCompleted={isCompleted}
              address={destination ? formatAddress(destination) : ''}
              rawAddress={destination}
              scheduledTime={destination?.scheduledTime}
              arrivalTime={destination?.arrivalTime}
              startSchedule={destination?.startSchedule}
              hasStartSchedule={hasStartSchedule}
              effectiveTime={destination?.effectiveTime}
              client={destination?.client}
              shouldReturnTravelRouteCard={shouldReturnTravelRouteCard}
              originColumnMd={originColumnMd}
            />
          </Row>
        );
      })}
      {waitingHours !== null && waitingMinutes !== null && (
        <Col className="text-center">
          <Text type="regular" color="gray">
            Tempo de espera total -
          </Text>
          <Text weight={500} type="regular">{` ${waitingHours}:${waitingMinutes}hrs`}</Text>
        </Col>
      )}
    </Card>
  );
}
const actions = {
  delivery: 'Entrega',
  customhouse: 'Aduaneiro',
  collect: 'Coleta',
};
function LocationItem({
  isCompleted,
  action,
  address,
  scheduledTime,
  arrivalTime,
  effectiveTime,
  startSchedule,
  hasStartSchedule,
  client,
  shouldReturnTravelRouteCard,
  originColumnMd,
}) {
  const [waitingTime, setWaitingTime] = useState();
  const schedulesColumnMd = 12 - originColumnMd;

  useEffect(() => {
    const diff = moment(effectiveTime).diff(moment(arrivalTime), 'minutes');
    if (diff < 0) {
      return;
    }
    const hours = parseInt(diff / 60, 10);
    const minutes = diff % 60;

    if (typeof diff === 'number') {
      if (hours < 10) {
        if (minutes < 10) setWaitingTime(`0${hours}:0${minutes}`);
        else setWaitingTime(`0${hours}:${minutes}`);
      } else if (minutes < 10) setWaitingTime(`${hours}:0${minutes}`);
      else setWaitingTime(`${hours}:${minutes}`);
    }
  }, [arrivalTime, effectiveTime]);

  return (
    <>
      <Styled.RouteLocationCol
        md={originColumnMd}
        xs={12}
        completed={effectiveTime}
        progress={arrivalTime}
      >
        <div className="d-flex">
          <div className="pt-2 d-flex">
            <Styled.Marker.Tray completed={effectiveTime} progress={arrivalTime} />
          </div>
          <Styled.LocationContent>
            <Styled.Marker completed={effectiveTime} progress={arrivalTime}>
              {isCompleted && <FaCheck color="#fff" size={9} />}
            </Styled.Marker>
            <Text as="div" color="dark">
              {action}
            </Text>
            <Text as="div" type="regular" color="gray">
              {address}
            </Text>
            {client ? (
              <Text as="div" type="regular" color="gray" className="my-2">
                {`Cliente: ${client?.socialName} - ${client?.cpfCnpj}`}
              </Text>
            ) : null}
          </Styled.LocationContent>
        </div>
      </Styled.RouteLocationCol>
      <Styled.Schedules
        xs={12}
        md={schedulesColumnMd}
        completed={effectiveTime}
        progress={arrivalTime}
        $showLeftDivider={shouldReturnTravelRouteCard}
      >
        {shouldReturnTravelRouteCard ? (
          <Styled.RouteColumnsGrid $hasStartSchedule={hasStartSchedule}>
            <Styled.RouteGridBodyCell>
              <div className="text-center w-100">
                <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                  Agendado
                </Text>
                <div className="d-flex flex-row justify-content-center">
                  {!!startSchedule && (
                    <>
                      <div>
                        <Text as="div" type="regular" color="dark">
                          {formatDateCustom(startSchedule, 'DD, MMMM [de] YYYY')}
                        </Text>
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                        >
                          <FaClock color={theme.colors.dark} />
                          <Text type="label" color="dark">
                            {formatTime(startSchedule)}
                          </Text>
                        </span>
                      </div>
                      <div className="mt-3 mx-2">
                        <FaArrowRight color={theme.colors.dark} />
                      </div>
                    </>
                  )}
                  <div>
                    <Text as="div" type="regular" color="dark">
                      {formatDateCustom(scheduledTime, 'DD, MMMM [de] YYYY')}
                    </Text>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <FaClock color={theme.colors.dark} />
                      <Text type="label" color="dark">
                        {formatTime(scheduledTime)}
                      </Text>
                    </span>
                  </div>
                </div>
              </div>
            </Styled.RouteGridBodyCell>
            <Styled.RouteGridBodyCell $showDivider>
              <div className="text-center w-100">
                <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                  Chegada
                </Text>
                {arrivalTime ? (
                  <>
                    <Text as="div" type="regular" color="dark">
                      {formatDateCustom(arrivalTime, 'DD, MMMM [de] YYYY')}
                    </Text>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <FaClock color={theme.colors.dark} />
                      <Text type="label" color="dark">
                        {formatTime(arrivalTime)}
                      </Text>
                    </span>
                  </>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <FaClock color={theme.colors.dark} />
                    <Text type="label" color="dark">
                      Não informado
                    </Text>
                  </span>
                )}
              </div>
            </Styled.RouteGridBodyCell>
            <Styled.RouteGridBodyCell>
              <div className="text-center w-100">
                {arrivalTime && effectiveTime && waitingTime && (
                  <span
                    title="Tempo de espera"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FaArrowRight color={theme.colors.dark} />
                    <FaClock color={theme.colors.dark} />
                    <Text type="label" color="dark">
                      {waitingTime}
                    </Text>
                    <FaArrowRight color={theme.colors.dark} />
                  </span>
                )}
              </div>
            </Styled.RouteGridBodyCell>
            <Styled.RouteGridBodyCell>
              <div className="text-center w-100">
                <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                  Efetivado
                </Text>
                {effectiveTime ? (
                  <>
                    <Text as="div" type="regular" color="dark">
                      {formatDateCustom(effectiveTime, 'DD, MMMM [de] YYYY')}
                    </Text>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <FaClock color={theme.colors.dark} />
                      <Text type="label" color="dark">
                        {formatTime(effectiveTime)}
                      </Text>
                    </span>
                  </>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <FaClock color={theme.colors.dark} />
                    <Text type="label" color="dark">
                      Não informado
                    </Text>
                  </span>
                )}
              </div>
            </Styled.RouteGridBodyCell>
          </Styled.RouteColumnsGrid>
        ) : (
          <div className="text-center">
            <Text as="div" color="dark" className="d-block d-sm-none mb-3">
              Agendado
            </Text>
            <div className="d-flex flex-row justify-content-center">
              {!!startSchedule && (
                <>
                  <div>
                    <Text as="div" type="regular" color="dark">
                      {formatDateCustom(startSchedule, 'DD, MMMM [de] YYYY')}
                    </Text>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <FaClock color={theme.colors.dark} />
                      <Text type="label" color="dark">
                        {formatTime(startSchedule)}
                      </Text>
                    </span>
                  </div>
                  <div className="mt-3 mx-2">
                    <FaArrowRight color={theme.colors.dark} />
                  </div>
                </>
              )}
              <div>
                <Text as="div" type="regular" color="dark">
                  {formatDateCustom(scheduledTime, 'DD, MMMM [de] YYYY')}
                </Text>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <FaClock color={theme.colors.dark} />
                  <Text type="label" color="dark">
                    {formatTime(scheduledTime)}
                  </Text>
                </span>
              </div>
            </div>
          </div>
        )}
      </Styled.Schedules>
    </>
  );
}
